import {
  Box,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CustomTitle from "../../shared/CustomTitle";
import MyTable from "../../shared/Tables/MyTable/MyTable";
import table from "../../shared/Tables/table.module.scss";
import { GET_DEVICE_STARTED_SESSIONS } from "../../../redux/types/shared.types";
import { devicesActions } from "../../../redux/slice/devices.slice";
import getSocket from "../../../redux/saga/socket";
import { TOPICS } from "../../utils/enums";
import { getDeviceSessionsServ } from "../../../redux/services/single.service";

const FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "apps", label: "Apps", filterType: "App" },
  { key: "file", label: "File Operations", filterType: "FileOperations" },
];

const formatDetails = (details) => {
  if (!details) return "-";
  if (typeof details === "string") return details;
  if (typeof details === "object") {
    const parts = [];

    if (details?.User) {
      parts.push(` ${details.User} ->`);
    }

    if (details?.Action) {
      parts.push(details.Action);
    }
    if (details?.AppName) {
      parts.push(` ${details.AppName}`);
    }
    if (details?.Pid) {
      parts.push(` ( ${details.Pid})`);
    }

    if (parts.length) {
      return parts.join(" ");
    }
  }
  return "-";
};

const DeviceSession = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [activityFilter, setActivityFilter] = useState("all");
  const selectedFilterOption = useMemo(
    () => FILTER_OPTIONS.find((option) => option.key === activityFilter),
    [activityFilter]
  );

  const deviceSession = useSelector((state) =>
    devicesActions.selectors.getDeviceSession(state, activityFilter)
  ) || { data: [] };

  const deviceStartedSessions = useSelector((state) =>
    devicesActions.selectors.getDeviceStartedSessions(state)
  ) || { data: [] };

  const { data: sessions = [] } = deviceSession;
  const selectedDevice = useSelector(
    devicesActions.selectors.getSelectedDevice
  );
  const computedDeviceUid = useMemo(() => {
    if (selectedDevice?.uid) {
      return selectedDevice.uid;
    }
    if (sessions?.length) {
      return sessions[0]?.uid;
    }
    return null;
  }, [selectedDevice, sessions]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const lastStartedRequestRef = useRef(null);
  const [activeSessionId, setActiveSessionId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setSelectedDate(dayjs().startOf("day"));
    setHasRequested(false);
    lastStartedRequestRef.current = null;
  }, [id]);

  const trimmedSearchTerm = searchTerm?.trim();
  const selectedFilterType = selectedFilterOption?.filterType;

  useEffect(() => {
    if (!id || !selectedDate) return;

    const nextSignature = JSON.stringify({
      deviceId: id,
      date: selectedDate.format("YYYY-MM-DD"),
      filterType: selectedFilterType,
      searchTerm: trimmedSearchTerm || "",
      activityFilter,
    });

    if (lastStartedRequestRef.current === nextSignature) {
      return;
    }

    lastStartedRequestRef.current = nextSignature;
    setLoading(true);
    setHasRequested(true);
    dispatch({
      type: GET_DEVICE_STARTED_SESSIONS,
      payload: {
        deviceId: id,
        filterType: selectedFilterType,
        filterKey: activityFilter,
        searchTerm: trimmedSearchTerm || undefined,
        date: selectedDate.format("YYYY-MM-DD"),
      },
    });
  }, [
    activityFilter,
    dispatch,
    id,
    selectedDate,
    selectedFilterType,
    trimmedSearchTerm,
  ]);

  useEffect(() => {
    if (!id) return;

    const payload = {
      deviceId: id,
      page: 1,
      pageSize: 25,
      filterType: selectedFilterType,
      searchTerm: trimmedSearchTerm || undefined,
    };

    getDeviceSessionsServ(payload).catch((error) => {
      console.log("Failed to fetch legacy device sessions", error);
    });
  }, [id, selectedFilterType, trimmedSearchTerm]);

  useEffect(() => {
    if (!hasRequested) return;
    setLoading(false);
  }, [sessions, hasRequested]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !computedDeviceUid) return undefined;

    const handleUserActions = (payload = {}) => {
      debugger;
      const rawMessage = payload?.message ?? payload;
      let parsed = rawMessage;

      if (typeof rawMessage === "string") {
        try {
          parsed = JSON.parse(rawMessage);
        } catch (error) {
          console.log("USER_ACTIONS", rawMessage);
          return;
        }
      }

      if (!parsed || typeof parsed !== "object") {
        console.log("USER_ACTIONS", rawMessage);
        return;
      }

      const eventUid = parsed?.uid;
      if (!eventUid || eventUid !== computedDeviceUid) {
        return;
      }

      const activity = parsed?.activity || {};
      const requiredFilter = selectedFilterOption?.filterType;
      if (requiredFilter && activity?.type !== requiredFilter) {
        return;
      }
      const activityTimestamp =
        activity?.timestamp || parsed?.lastSeenAt || dayjs().toISOString();
      const normalizedTimestamp = dayjs(activityTimestamp).isValid()
        ? dayjs(activityTimestamp).toISOString()
        : dayjs().toISOString();

      const liveSession = {
        ...parsed,
        sessionId:
          parsed?.sessionId || parsed?._id || `${eventUid}-${Date.now()}`,
        uid: eventUid,
        activity: {
          ...activity,
          timestamp: normalizedTimestamp,
        },
        lastSeenAt: normalizedTimestamp,
        startedAt: parsed?.startedAt || normalizedTimestamp,
      };

      dispatch(
        devicesActions.actions.appendDevicesession({
          session: liveSession,
          filterKey: activityFilter,
        })
      );
    };

    socket.on(TOPICS.USER_ACTIONS, handleUserActions);
    return () => {
      socket.off(TOPICS.USER_ACTIONS, handleUserActions);
    };
  }, [computedDeviceUid, dispatch, activityFilter, selectedFilterOption]);

  const rows = useMemo(() => {
    return (sessions || []).map((session, index) => ({
      id: session?.sessionId || session?._id || `${index}-${session?.uid}`,
      uid: session?.uid,
      activityType: session?.activity?.type || "-",
      activityDetails: formatDetails(session?.activity?.details),
      activityTimestamp: session?.activity?.timestamp || session?.lastSeenAt,
      lastSeenAt: session?.lastSeenAt,
    }));
  }, [sessions]);

  const sessionSummaries = useMemo(
    () =>
      (deviceStartedSessions.data || [])
        .filter((session) => Boolean(session?.startTime))
        .map((session) => ({
          id: session.sessionId,
          startTime: session.startTime,
          ip: session.ip,
          deviceId: session.deviceId,
        })),
    [deviceStartedSessions.data]
  );

  useEffect(() => {
    if (!sessionSummaries.length) {
      setActiveSessionId(null);
      return;
    }
    setActiveSessionId((prev) => {
      if (prev && sessionSummaries.find((session) => session.id === prev)) {
        return prev;
      }
      return sessionSummaries[0]?.id || null;
    });
  }, [sessionSummaries]);

  const activeSession = useMemo(
    () =>
      sessionSummaries.find((session) => session.id === activeSessionId) ||
      null,
    [sessionSummaries, activeSessionId]
  );

  const columns = useMemo(
    () => [
      {
        field: "activityType",
        headerName: "Activity Type",
        minWidth: 140,
        flex: 1,
      },
      {
        field: "activityDetails",
        headerName: "Details",
        minWidth: 280,
        flex: 2,
      },
      {
        field: "activityTimestamp",
        headerName: "Activity Time",
        minWidth: 180,
        flex: 1,
        // valueGetter: ({ row }) => formatDateTime(row.activityTimestamp),
      },
      {
        field: "lastSeenAt",
        headerName: "Last Seen",
        minWidth: 180,
        flex: 1,
        // valueGetter: ({ row }) => formatDateTime(row.lastSeenAt),
      },
    ],
    []
  );

  const displayedRowCount = rows.length;
  return (
    <div className={table.tableContainer}>
      <CustomTitle title={"Device Sessions"} count={displayedRowCount} />
      <Box mb={2}>
        <Card sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            flexWrap="wrap"
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Active Session
              </Typography>
              {activeSession ? (
                <Typography variant="body2">
                  {dayjs(activeSession.startTime).format("MMM D, YYYY h:mm A")}
                  {activeSession.deviceId ? ` • ${activeSession.deviceId}` : ""}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No sessions recorded for this date.
                </Typography>
              )}
              {activeSession?.ip && (
                <Typography variant="caption" color="text.secondary">
                  {activeSession.ip}
                </Typography>
              )}
            </Box>
            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel id="active-session-select-label">
                Select session
              </InputLabel>
              <Select
                labelId="active-session-select-label"
                value={activeSessionId || ""}
                label="Select session"
                onChange={(event) => setActiveSessionId(event.target.value)}
              >
                {sessionSummaries.map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    {dayjs(session.startTime).format("MMM D, YYYY h:mm A")}
                    {session.deviceId ? ` • ${session.deviceId}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Card>
      </Box>
      <Box mb={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Stack direction="row" spacing={1}>
            {FILTER_OPTIONS.map((option) => (
              <Button
                key={option.key}
                variant={
                  activityFilter === option.key ? "contained" : "outlined"
                }
                size="small"
                onClick={() => setActivityFilter(option.key)}
                sx={{ textTransform: "none" }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Session Date"
                disableFuture
                value={selectedDate}
                onChange={(value) => {
                  if (!value) return;
                  setSelectedDate(value.startOf("day"));
                }}
                slotProps={{
                  textField: { size: "small" },
                }}
              />
            </LocalizationProvider>
            <TextField
              size="small"
              placeholder="Search events"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 240 } }}
            />
          </Stack>
        </Stack>
      </Box>
      <Card>
        <MyTable
          id="device-session-table"
          rows={rows}
          columns={columns}
          isMultiLine
          pagination={false}
          externalLoading={loading}
        />
      </Card>
    </div>
  );
};

export default DeviceSession;
